/**
 * Every route in App.tsx is React.lazy()-loaded. A deploy renames chunk
 * files (content-hashed), so a tab left open on the old index.html 404s the
 * first time it dynamically imports a route it hadn't loaded yet - surfaced
 * either as Vite's own 'vite:preloadError' event (main.tsx) or as a thrown
 * error reaching ErrorBoundary, depending on exactly how the import failed.
 * Both paths share this guard so a genuinely broken deploy reloads once,
 * not forever.
 */
const CHUNK_RELOAD_KEY = 'cg-chunk-reload-attempted';

const STALE_CHUNK_PATTERN =
  /loading chunk|dynamically imported module|importing a module script failed|unable to preload css/i;

export function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return STALE_CHUNK_PATTERN.test(message);
}

// Returns true if it triggered a reload (caller should not also render its
// own fallback UI - a reload is already in flight).
export function reloadOnceForStaleChunk(): boolean {
  if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) return false;
  sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
  window.location.reload();
  return true;
}

// Called once the app has rendered successfully, so a later, unrelated
// stale-chunk failure later in the same tab's lifetime still gets its one
// automatic reload rather than being permanently blocked by an old guard.
export function clearStaleChunkGuard(): void {
  sessionStorage.removeItem(CHUNK_RELOAD_KEY);
}
